import { Check, FileText, RotateCcw, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { ApiError, getCodeStandard, reinferCodeStandard, saveCodeStandard } from "@/api/client";
import type { CodeStandard, CodeStandardDef, Segment } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { EmptyState } from "@/ui/EmptyState";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Card, Skeleton } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

/** Where a click on a legend text box puts its words: a value's meaning, or a flag's. */
type Target = { segment: string; value: string } | { flag: string };

const FLAGS = "flags";

function sameTarget(a: Target | null, b: Target): boolean {
  if (!a) return false;
  if ("flag" in a) return "flag" in b && a.flag === b.flag;
  return "segment" in b && a.segment === b.segment && a.value === b.value;
}

function withMeaning(def: CodeStandardDef, target: Target, meaning: string): CodeStandardDef {
  if ("flag" in target) {
    return {
      ...def,
      flags: { ...def.flags, [target.flag]: meaning },
      suggested_flags: (def.suggested_flags ?? []).filter((f) => f !== target.flag),
    };
  }
  return withSegment(def, target.segment, (s) => ({
    ...s,
    values: { ...s.values, [target.value]: meaning },
    suggested: (s.suggested ?? []).filter((v) => v !== target.value),
  }));
}

function withSegment(
  def: CodeStandardDef,
  key: string,
  change: (s: Segment) => Segment,
): CodeStandardDef {
  return {
    ...def,
    tokens: def.tokens.map((token) => token.map((s) => (s.key === key ? change(s) : s))),
  };
}

/** The standard's shape in one line: `[Receptor][Bypass][Jamb]-[Lites]-[Child] + P S`. */
function Anatomy({ def }: { def: CodeStandardDef }) {
  return (
    <p className="flex flex-wrap items-center gap-1 font-mono text-sm" aria-label="Code anatomy">
      {def.tokens.map((token, i) => (
        <span key={token.map((s) => s.key).join()} className="flex items-center gap-1">
          {i > 0 ? <span className="text-muted">{def.separator}</span> : null}
          {token.map((s) => (
            <span key={s.key} className="rounded bg-accent-soft px-1.5 py-0.5 text-accent">
              {s.label}
            </span>
          ))}
        </span>
      ))}
      {Object.keys(def.flags ?? {}).length > 0 ? (
        <span className="ml-1 text-muted">+ {Object.keys(def.flags ?? {}).join(" ")}</span>
      ) : null}
    </p>
  );
}

interface ValuesProps {
  label: string;
  values: Record<string, string>;
  suggested: string[];
  usage: Record<string, number>;
  focused: Target | null;
  targetOf: (value: string) => Target;
  onFocus: (t: Target) => void;
  onMeaning: (t: Target, meaning: string) => void;
}

function ValuesTable({
  label,
  values,
  suggested,
  usage,
  focused,
  targetOf,
  onFocus,
  onMeaning,
}: ValuesProps) {
  return (
    <Table aria-label={label}>
      <thead>
        <tr>
          <Th className="w-20">Value</Th>
          <Th>Meaning</Th>
          <Th className="w-28 text-right">Past boxes</Th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(values).map(([value, meaning]) => {
          const target = targetOf(value);
          return (
            <tr key={value} className={sameTarget(focused, target) ? "bg-accent-soft/40" : ""}>
              <Td className="font-mono font-semibold text-ink">{value}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={`${label} ${value} meaning`}
                    value={meaning}
                    placeholder="What it means"
                    onFocus={() => onFocus(target)}
                    onChange={(e) => onMeaning(target, e.target.value)}
                  />
                  {suggested.includes(value) ? (
                    <Badge tone="violet" title="Suggested by the legend">
                      Suggested
                    </Badge>
                  ) : null}
                </div>
              </Td>
              <Td className="tabular text-right text-ink-2">
                {(usage[value] ?? 0).toLocaleString("en-US")}
              </Td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

/**
 * How the organization's codes are built (ADR-0014, Phase 7 K3). Inferred from the codes
 * on its past take-offs, offered meanings by its legend, and confirmed here once. A code
 * that does not decode is listed, never bent to fit.
 */
export function CodeStandardTab() {
  const [standard, setStandard] = useState<CodeStandard | null>(null);
  const [draft, setDraft] = useState<CodeStandardDef | null>(null);
  const [focused, setFocused] = useState<Target | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const canManage = useCan("knowledge:manage");

  function take(got: CodeStandard) {
    setStandard(got);
    setDraft(got.definition ?? null);
  }

  useEffect(() => {
    let cancelled = false;
    getCodeStandard()
      .then((got) => {
        if (!cancelled) take(got);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof ApiError ? err.detail : "Could not reach the server.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function act(call: () => Promise<CodeStandard>) {
    setSaving(true);
    setError(null);
    try {
      take(await call());
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not reach the server.");
    } finally {
      setSaving(false);
    }
  }

  function setMeaning(target: Target, meaning: string) {
    setDraft((d) => (d ? withMeaning(d, target, meaning) : d));
  }

  if (standard === null || (standard.status !== "none" && draft === null)) {
    return error ? (
      <Notice tone="danger">{error}</Notice>
    ) : (
      <div className="space-y-2" role="status" aria-label="Loading the code standard">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (standard.status === "none" || draft === null || draft.tokens.length === 0) {
    return (
      <EmptyState icon={<FileText />} title="No codes to learn from yet">
        Add a past take-off under <Link to="/knowledge/sources">Sources</Link>. Its codes show how
        your company builds them, and a code legend, if you have one, says what each part means.
      </EmptyState>
    );
  }

  const confirmed = standard.status === "confirmed";
  const from = standard.inferred_from;
  const segments = draft.tokens.flat();

  return (
    <section aria-label="Code standard" className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="flex min-w-0 flex-col gap-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Badge tone={confirmed ? "ok" : "violet"} dot>
                {confirmed ? "Confirmed" : "Proposed"}
              </Badge>
              {confirmed && standard.confirmed_by ? (
                <span className="text-xs text-muted">by {standard.confirmed_by}</span>
              ) : null}
            </div>
            <Anatomy def={draft} />
            {from.codes ? (
              <p className="text-xs text-muted">
                Read from {from.codes.toLocaleString("en-US")} codes on{" "}
                {(from.boxes ?? 0).toLocaleString("en-US")} boxes in {from.sources ?? 0} past
                take-off{from.sources === 1 ? "" : "s"}.
              </p>
            ) : null}
          </div>
          {canManage ? (
            <div className="flex gap-2">
              {confirmed ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={saving}
                  onClick={() => void act(reinferCodeStandard)}
                >
                  <RotateCcw aria-hidden="true" />
                  Infer again
                </Button>
              ) : null}
              <Button
                size="sm"
                disabled={saving}
                onClick={() => void act(() => saveCodeStandard(draft))}
              >
                <Check aria-hidden="true" />
                {confirmed ? "Save changes" : "Confirm standard"}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted">An owner or admin confirms and changes it.</p>
          )}
        </header>

        {error ? <Notice tone="danger">{error}</Notice> : null}

        {standard.undecoded.length > 0 ? (
          <Notice tone="warn">
            {standard.undecoded.length} code{standard.undecoded.length === 1 ? "" : "s"} in past
            take-offs do not fit:{" "}
            <span className="font-mono">
              {standard.undecoded.map((u) => `${u.code} (${u.boxes})`).join(", ")}
            </span>
          </Notice>
        ) : null}

        {segments.map((segment) => (
          <Card key={segment.key} className="flex flex-col gap-3 p-4">
            <label className="flex max-w-xs flex-col gap-1 text-xs font-medium text-muted">
              Part
              <Input
                aria-label={`Name of part ${segment.key}`}
                value={segment.label}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? withSegment(d, segment.key, (s) => ({ ...s, label: e.target.value })) : d,
                  )
                }
              />
            </label>
            {segment.kind === "number" ? (
              <p className="text-sm text-ink-2">
                Any {segment.width}-digit number. Seen:{" "}
                <span className="font-mono">
                  {Object.keys(standard.usage[segment.key] ?? {})
                    .sort()
                    .join(", ") || "none yet"}
                </span>
              </p>
            ) : (
              <ValuesTable
                label={segment.label}
                values={segment.values}
                suggested={segment.suggested ?? []}
                usage={standard.usage[segment.key] ?? {}}
                focused={focused}
                targetOf={(value) => ({ segment: segment.key, value })}
                onFocus={setFocused}
                onMeaning={setMeaning}
              />
            )}
          </Card>
        ))}

        {Object.keys(draft.flags ?? {}).length > 0 ? (
          <Card className="flex flex-col gap-3 p-4">
            <h3 className="text-xs font-medium text-muted">Flags at the end</h3>
            <ValuesTable
              label="Flag"
              values={draft.flags ?? {}}
              suggested={draft.suggested_flags ?? []}
              usage={standard.usage[FLAGS] ?? {}}
              focused={focused}
              targetOf={(flag) => ({ flag })}
              onFocus={setFocused}
              onMeaning={setMeaning}
            />
          </Card>
        ) : null}
      </div>

      <aside aria-label="Legend" className="flex flex-col gap-3">
        <h3 className="flex items-center gap-2 text-sm font-medium text-ink">
          <Sparkles aria-hidden="true" className="size-4 text-violet" />
          Legend
        </h3>
        {standard.legend ? (
          <>
            <p className="text-xs text-muted">
              From{" "}
              <Link to={`/knowledge/sources/${standard.legend.document_id}`}>
                {standard.legend.filename}
              </Link>
              . Pick a meaning field, then a text box to fill it.
            </p>
            <ul className="flex flex-col gap-1.5">
              {standard.legend.texts.map((t, i) => (
                <li key={`${t.page_index}-${i}`}>
                  <button
                    type="button"
                    disabled={!focused}
                    onClick={() => focused && setMeaning(focused, t.text)}
                    className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-left text-xs text-ink-2 enabled:hover:border-accent disabled:cursor-default"
                  >
                    {t.text}
                    {t.used ? (
                      <span className="mt-0.5 block text-[11px] text-violet">
                        Suggested meanings
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-xs text-muted">
            No legend yet. If your company has a code legend, add it under{" "}
            <Link to="/knowledge/sources">Sources</Link> as a code standard, and its words are
            offered here.
          </p>
        )}
      </aside>
    </section>
  );
}
